import React, {useCallback, useState, useMemo, useEffect} from 'react'
import { Button, message, Space, Modal, Table, Select, Typography, Card, Checkbox } from 'antd'
import useStore from '../../../stores/useStore'
import { InboxOutlined, EyeOutlined, DownloadOutlined, PlusOutlined } from "@ant-design/icons"
import Dragger from "antd/es/upload/Dragger"
import Papa from "papaparse"

const { Text } = Typography
const { Option } = Select

const ImportDataControl = () => {
    const {
        setCSVParsedData,
        setCSVFileNameColumn,
        confirmCSVData,
        cancelCSVImport,
        csvParsedData = [],
        textBlocks
    } = useStore()

    const [isModalVisible, setIsModalVisible] = useState(false)
    const [selectedColumn, setSelectedColumn] = useState(null)
    const [fileName, setFileName] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)
    const [autoCreateBlocks, setAutoCreateBlocks] = useState(true)
    const [selectedColumnsForBlocks, setSelectedColumnsForBlocks] = useState([])

    const csvColumns = useMemo(() => {
        if (!csvParsedData || csvParsedData.length === 0) return []

        const firstRow = csvParsedData[0]
        if (!firstRow || typeof firstRow !== 'object') return []

        return Object.keys(firstRow).filter(key =>
            key && key.trim() !== '' &&
            key !== 'id' &&
            key !== 'importedAt' &&
            key !== 'key'
        )
    }, [csvParsedData])

    const existingKeys = useMemo(() => {
        return new Set(textBlocks.map(block => block.key).filter(key => key && key.trim() !== ''))
    }, [textBlocks])

    useEffect(() => {
        if (csvColumns.length > 0) {
            setSelectedColumnsForBlocks(csvColumns)

            if (!selectedColumn && csvColumns[0]) {
                setSelectedColumn(csvColumns[0])
            }
        }
    }, [csvColumns])

    const parseCSV = (csvData) => {
        if (!csvData || !Array.isArray(csvData)) return []

        return csvData.map(row => {
            if (!row) return {}

            const result = {}
            Object.keys(row).forEach(key => {
                result[key] = row[key]
            })
            return result
        })
    }

    const handleFileRead = (file) => {
        setIsProcessing(true)
        const reader = new FileReader()
        setFileName(file.name || 'unknown.csv')

        reader.onload = (e) => {
            try {
                setIsProcessing(false)

                if (!e.target?.result) {
                    message.error('Файл пуст или не может быть прочитан')
                    return
                }

                const results = Papa.parse(e.target.result, {
                    header: true,
                    skipEmptyLines: true,
                    encoding: 'UTF-8',
                    dynamicTyping: true
                })

                if (!results.data || !Array.isArray(results.data)) {
                    message.error('Не удалось распарсить CSV файл')
                    return
                }

                const parsedData = parseCSV(results.data)

                if (parsedData.length === 0) {
                    message.warning('CSV файл не содержит данных')
                    return
                }

                const firstRow = parsedData[0]
                const columns = firstRow ? Object.keys(firstRow) : []

                if (columns.length === 0) {
                    message.error('CSV файл не содержит колонок с данными')
                    return
                }

                setCSVParsedData(parsedData)

                setIsModalVisible(true)

                message.success(`CSV файл загружен. Найдено ${parsedData.length} записей, ${columns.length} колонок`)
            } catch (error) {
                setIsProcessing(false)
                message.error('Ошибка при обработке CSV файла')
                console.error('CSV parsing error:', error)
            }
        }

        reader.onerror = () => {
            setIsProcessing(false)
            message.error('Ошибка чтения файла')
        }

        reader.readAsText(file, 'UTF-8')
    }

    const beforeUpload = useCallback((file) => {
        if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
            message.error('Пожалуйста, загрузите CSV файл')
            return false
        }

        handleFileRead(file)
        return false
    }, [])

    const handleDownloadTemplate = () => {
        try {
            const templateUrl = '/templates/example_small.csv'

            const link = document.createElement('a')
            link.href = templateUrl
            link.download = 'шаблон_данных.csv'
            link.style.display = 'none'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            message.success('Шаблон скачан. Заполните его своими данными и загрузите обратно.')
        } catch (error) {
            console.error('Ошибка при скачивании шаблона:', error)
            message.error('Не удалось скачать шаблон')
        }
    }

    const handleConfirmImport = () => {
        if (!selectedColumn) {
            message.warning('Пожалуйста, выберите колонку для именования файлов')
            return
        }

        if (!csvParsedData || csvParsedData.length === 0) {
            message.error('Нет данных для импорта')
            return
        }

        setCSVFileNameColumn(selectedColumn)

        const columnsForBlocks = autoCreateBlocks ? selectedColumnsForBlocks : []
        const newBlocks = confirmCSVData(columnsForBlocks)

        setIsModalVisible(false)

        let successMessage = `Данные импортированы (${csvParsedData.length} записей). Файлы будут названы по колонке "${selectedColumn}"`

        if (autoCreateBlocks && newBlocks && newBlocks.length > 0) {
            successMessage += `. Создано ${newBlocks.length} новых текстовых блоков.`
        }

        message.success(successMessage)
    }

    const handleCancelImport = () => {
        cancelCSVImport()
        setIsModalVisible(false)
        setSelectedColumn(null)
        setSelectedColumnsForBlocks([])
        message.info('Импорт отменен')
    }

    const handleSelectAllColumns = () => {
        setSelectedColumnsForBlocks(csvColumns)
    }

    const handleDeselectAllColumns = () => {
        setSelectedColumnsForBlocks([])
    }

    const handleToggleColumn = (column) => {
        if (selectedColumnsForBlocks.includes(column)) {
            setSelectedColumnsForBlocks(selectedColumnsForBlocks.filter(c => c !== column))
        } else {
            setSelectedColumnsForBlocks([...selectedColumnsForBlocks, column])
        }
    }

    const tableColumns = useMemo(() => {
        const columns = []

        if (csvColumns && csvColumns.length > 0) {
            csvColumns.forEach(column => {
                if (column) {
                    columns.push({
                        title: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Checkbox
                                    checked={selectedColumnsForBlocks.includes(column)}
                                    onChange={() => handleToggleColumn(column)}
                                />
                                <span>{column}</span>
                                {existingKeys.has(column) && (
                                    <Text type="secondary" style={{ fontSize: '10px' }}>
                                        (есть блок)
                                    </Text>
                                )}
                            </div>
                        ),
                        dataIndex: column,
                        key: column,
                        render: (text) => (
                            <div style={{
                                maxWidth: 200,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'help',
                                position: 'relative'
                            }} title={String(text || '')}>
                                {text !== undefined && text !== null && text !== ''
                                    ? String(text)
                                    : <Text type="secondary">(пусто)</Text>
                                }
                            </div>
                        )
                    })
                }
            })
        }

        if (columns.length > 0) {
            columns.unshift({
                title: '#',
                key: 'index',
                render: (_, __, index) => index + 1,
                width: 50,
            })
        }

        return columns
    }, [csvColumns, selectedColumnsForBlocks, existingKeys])

    const hasCSVData = csvParsedData && csvParsedData.length > 0
    const hasColumns = csvColumns && csvColumns.length > 0
    const tableDataSource = useMemo(() => {
        if (!hasCSVData) return []
        return csvParsedData.map((item, index) => ({
            ...item,
            key: index,
        }))
    }, [csvParsedData, hasCSVData])

    const renderBlockPreview = () => {
        const previewBlocks = selectedColumnsForBlocks
            .filter(column => !existingKeys.has(column))
            .slice(0, 5) // Показываем только первые 5

        if (previewBlocks.length === 0) {
            return null
        }

        return (
            <div style={{ marginTop: 16, padding: 12, background: '#f0f5ff', borderRadius: 4, border: '1px solid #d6e4ff' }}>
                <Text strong>Будут созданы текстовые блоки:</Text>
                <div style={{ marginTop: 8 }}>
                    {previewBlocks.map((column, index) => (
                        <div key={column} style={{
                            marginBottom: 4,
                            padding: '4px 8px',
                            background: 'white',
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <PlusOutlined style={{ color: '#52c41a' }} />
                            <Text code>{column}</Text>
                            <Text type="secondary">→</Text>
                            <Text>{`{${column}}`}</Text>
                        </div>
                    ))}
                    {selectedColumnsForBlocks.filter(c => !existingKeys.has(c)).length > 5 && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                            ... и ещё {selectedColumnsForBlocks.filter(c => !existingKeys.has(c)).length - 5} блоков
                        </Text>
                    )}
                </div>
            </div>
        )
    }

    return (
        <>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Dragger
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept=".csv"
                    maxCount={1}
                    disabled={isProcessing}
                >
                    <p className="ant-upload-drag-icon">
                        {isProcessing ? <Text type="secondary">Загрузка...</Text> : <InboxOutlined />}
                    </p>
                    <p className="ant-upload-text">
                        {isProcessing ? 'Обработка файла...' : 'Перетащите CSV или выберите его'}
                    </p>
                    <p className="ant-upload-hint">
                        Поддерживается загрузка только одного CSV файла.
                    </p>
                </Dragger>

                <Button
                    className="download-template-button"
                    type="default"
                    icon={<DownloadOutlined />}
                    block
                    onClick={handleDownloadTemplate}
                    style={{ marginTop: '8px' }}
                >
                    Скачать шаблон CSV
                </Button>

                {hasCSVData && (
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        block
                        onClick={() => setIsModalVisible(true)}
                        loading={isProcessing}
                        style={{ marginTop: '8px' }}
                    >
                        Просмотреть загруженные данные ({csvParsedData.length} записей)
                    </Button>
                )}
            </Space>

            <Modal
                title={
                    <div>
                        <InboxOutlined style={{ marginRight: 8 }} />
                        Настройка импорта CSV
                        {fileName && (
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                {fileName}
                            </Text>
                        )}
                    </div>
                }
                open={isModalVisible}
                onCancel={handleCancelImport}
                width="90%"
                style={{ top: 20 }}
                footer={[
                    <Button key="cancel" onClick={handleCancelImport}>
                        Отмена
                    </Button>,
                    <Button
                        key="import"
                        type="primary"
                        onClick={handleConfirmImport}
                        disabled={!selectedColumn || !hasCSVData}
                        loading={isProcessing}
                    >
                        Подтвердить импорт
                    </Button>
                ]}
            >
                {!hasCSVData ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Text type="secondary">Нет данных для отображения</Text>
                    </div>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* Секция настройки автоматического создания блоков */}
                        <Card
                            size="small"
                            title="Настройка текстовых блоков"
                            style={{ marginBottom: 16 }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Checkbox
                                    checked={autoCreateBlocks}
                                    onChange={(e) => setAutoCreateBlocks(e.target.checked)}
                                >
                                    <Text strong>Автоматически создать текстовые блоки из CSV колонок</Text>
                                </Checkbox>

                                {autoCreateBlocks && (
                                    <>
                                        <div style={{ marginTop: 8 }}>
                                            <Text>Выберите колонки для создания текстовых блоков:</Text>
                                            <div style={{ marginTop: 8, display: 'flex', gap: '8px' }}>
                                                <Button size="small" onClick={handleSelectAllColumns}>
                                                    Выбрать все
                                                </Button>
                                                <Button size="small" onClick={handleDeselectAllColumns}>
                                                    Снять все
                                                </Button>
                                            </div>
                                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                <div>• Блоки будут созданы только для выбранных колонок</div>
                                                <div>• Колонки с существующими блоками будут проигнорированы</div>
                                                <div>• В тексте блоков будет использован шаблон {'{название_колонки}'}</div>
                                            </div>
                                        </div>

                                        {/* Предпросмотр создаваемых блоков */}
                                        {renderBlockPreview()}
                                    </>
                                )}
                            </Space>
                        </Card>

                        {/* Секция именования файлов */}
                        <Card
                            size="small"
                            title="Настройка именования файлов"
                            style={{ marginBottom: 16 }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text>
                                    Выберите колонку, по которой будут называться сгенерированные файлы:
                                </Text>
                                <Select
                                    placeholder="Выберите колонку для именования файлов"
                                    style={{ width: '100%' }}
                                    value={selectedColumn}
                                    onChange={setSelectedColumn}
                                    disabled={!hasColumns}
                                >
                                    {hasColumns && csvColumns.map(column => (
                                        <Option key={column} value={column}>
                                            {column}
                                            {existingKeys.has(column) && (
                                                <Text type="secondary"> (есть текстовый блок)</Text>
                                            )}
                                        </Option>
                                    ))}
                                </Select>
                                {selectedColumn && (
                                    <Text type="secondary">
                                        Файлы будут названы по значениям из колонки "{selectedColumn}"
                                    </Text>
                                )}
                            </Space>
                        </Card>

                        {/* Просмотр данных */}
                        <Card
                            size="small"
                            title={`Данные (${csvParsedData.length} записей, ${csvColumns.length} колонок)`}
                        >
                            <div style={{ maxHeight: 400, overflow: 'auto' }}>
                                {tableColumns.length > 0 && tableDataSource.length > 0 ? (
                                    <Table
                                        columns={tableColumns}
                                        dataSource={tableDataSource}
                                        size="small"
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showQuickJumper: true,
                                        }}
                                        scroll={{ y: 300 }}
                                        loading={isProcessing}
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                        <Text type="secondary">
                                            {hasCSVData
                                                ? 'Не удалось определить структуру данных'
                                                : 'Нет данных для отображения в таблице'
                                            }
                                        </Text>
                                        {hasCSVData && (
                                            <div style={{ marginTop: 10 }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    Первая строка данных: {JSON.stringify(csvParsedData[0])}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Пример именования файлов */}
                            {selectedColumn && csvParsedData.length > 0 && (
                                <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 4 }}>
                                    <Text strong>Пример именования файлов:</Text>
                                    <div style={{ marginTop: 8 }}>
                                        {csvParsedData.slice(0, 3).map((row, index) => {
                                            let fileName = `postcard_${index + 1}`
                                            if (row && row[selectedColumn]) {
                                                const columnValue = String(row[selectedColumn])
                                                fileName = columnValue
                                                    .replace(/[^\w\u0400-\u04FF\s\-]/gi, '_')
                                                    .replace(/\s+/g, '_')
                                                    .trim()

                                                if (!fileName) {
                                                    fileName = `postcard_${index + 1}`
                                                }
                                            }

                                            return (
                                                <div key={index} style={{ marginBottom: 4 }}>
                                                    <Text code>
                                                        {fileName}.png
                                                    </Text>
                                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                                        ← из колонки "{selectedColumn}": {row[selectedColumn] !== undefined && row[selectedColumn] !== null ?
                                                        `"${row[selectedColumn]}"` :
                                                        '(пустое значение)'
                                                    }
                                                    </Text>
                                                </div>
                                            )
                                        })}
                                        {csvParsedData.length > 3 && (
                                            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                                ... и ещё {csvParsedData.length - 3} файлов
                                            </Text>
                                        )}
                                    </div>

                                    {/* Инструкции */}
                                    <div style={{ marginTop: 12, padding: 8, background: '#e6f7ff', borderRadius: 4 }}>
                                        <Text strong>Как использовать данные CSV:</Text>
                                        <div style={{ fontSize: 12, marginTop: 4 }}>
                                            1. В текстовых блоках используйте {'{'}название_колонки{'}'} для подстановки значений
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                            2. Например, если в CSV есть колонка "name", то в тексте используйте: {'{'}<Text code>name</Text>{'}'}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                            3. Экспортируется только текущий выбранный фон
                                        </div>
                                        <div style={{ fontSize: 12, marginTop: 8 }}>
                                            4. <Button
                                            type="link"
                                            size="small"
                                            onClick={handleDownloadTemplate}
                                            icon={<DownloadOutlined />}
                                        >
                                            Скачать шаблон CSV
                                        </Button> для примеров формата
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Space>
                )}
            </Modal>
        </>
    )
}

export default ImportDataControl